import type { AnalyticsEvent } from "@octopusdeploy/portal-analytics";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { getPageFields, usePageInstanceSummary } from "@octopusdeploy/portal-routes";
import { useCallback, useRef } from "react";
import { Action } from "~/analytics/Analytics";

type KnownPackageUploadEvents = "Save Upload Package" | "Cancel uploading package" | "Upload Package";

interface UploadPackageAnalyticsEvent<T extends KnownPackageUploadEvents> extends AnalyticsEvent<T> {
    resource: "Package";
}

interface SaveUploadPackageAnalyticsEvent extends UploadPackageAnalyticsEvent<"Save Upload Package"> {
    action: Action.Save;
}

export function createLegacyUploadPackageSaveEvent(): SaveUploadPackageAnalyticsEvent {
    return { eventName: "Save Upload Package", action: Action.Save, resource: "Package" };
}

interface UploadPackageCancelledEvent extends UploadPackageAnalyticsEvent<"Cancel uploading package"> {
    action: Action.Cancel;
}

export function createLegacyUploadPackageCancelledEvent(): UploadPackageCancelledEvent {
    return {
        eventName: "Cancel uploading package",
        action: Action.Cancel,
        resource: "Package",
    };
}

interface UploadPackageStartedEvent extends UploadPackageAnalyticsEvent<"Upload Package"> {
    action: Action.Add;
}

export function createLegacyPackageUploadStartedEvent(): UploadPackageStartedEvent {
    return {
        eventName: "Upload Package",
        action: Action.Add,
        resource: "Package",
    };
}

export function useTrackLegacyPackageUploadEvent() {
    const track = useTrackEvent();
    const pageDetail = usePageInstanceSummary();
    const latestPageDetail = useRef(pageDetail);
    latestPageDetail.current = pageDetail;

    return useCallback(
        (event: UploadPackageCancelledEvent | UploadPackageStartedEvent | SaveUploadPackageAnalyticsEvent) => {
            track({
                ...(latestPageDetail.current ? getPageFields(latestPageDetail.current) : {}),
                ...event,
            });
        },
        [track]
    );
}
